import React from 'react'

import { FaPencilAlt as Pencil } from 'react-icons/fa'

import css from './editable-text.css'
import KeyboardListener, { KeyCode, KeyBinding } from '../keyboard-listener'

export type EditableTextChildrenProps = {
  className: string;
  style?: React.CSSProperties;
}

export type EditableTextProps = {
  onClick?: (event: React.MouseEvent<HTMLInputElement>) => void;
  onChange: (value: string) => void;
  defaultValue?: string;
  activeClassName?: string;
  editable?: boolean;
  editing?: boolean;
  children: (value: string, props: EditableTextChildrenProps) => React.ReactNode;
  singleClickEdit?: boolean;
  tabIndex?: number | null;
  className?: string;
  style?: React.CSSProperties;
  iconSize?: number;
  iconStyle?: React.CSSProperties;
  showIcon?: boolean;
}

function EditableText ({
  onClick,
  onChange,
  defaultValue,
  activeClassName = '',
  editable = true,
  editing = false,
  children,
  singleClickEdit = false,
  tabIndex = null,
  className = '',
  style,
  iconSize,
  iconStyle,
  showIcon = true,
}: EditableTextProps) {
  const [isEditing, setIsEditing] = React.useState(false)
  const [value, setValue] = React.useState(defaultValue || '')
  const inputRef = React.useRef<HTMLInputElement>(null)
  const noRightPaddingClass = !showIcon ? css.noRightPadding : ''

  React.useEffect(() => {
    if (editing) {
      setIsEditing(true)
    }

    if (isEditing && !editing) {
      setIsEditing(false)
    }
  }, [editing])

  React.useEffect(() => {
    if (isEditing) {
      handleFocus()
    }
  }, [isEditing])

  React.useEffect(() => {
    setValue(defaultValue || '')
  }, [defaultValue])

  function handleBlur () {
    setIsEditing(false)
    const trimmedValue = value.trim()
    if (trimmedValue && trimmedValue !== defaultValue) {
      onChange(trimmedValue)
    }
  }

  function handleFocus () {
    if (inputRef.current) {
      inputRef.current.focus()
      inputRef.current.setSelectionRange(0, value.length)
    }
  }

  function toggleEdit () {
    if (!editable) return
    setIsEditing(!isEditing)
  }

  function handleEscKey () {
    setValue(defaultValue || '')
    setIsEditing(false)
  }

  if (isEditing) {
    return (
      <>
        <input
          ref={inputRef}
          tabIndex={tabIndex ?? undefined}
          type='text'
          className={`width-100 ${className.replace('truncate', '')} ${activeClassName} ${css.text} ${noRightPaddingClass}`}
          onChange={e => setValue(e.target.value)}
          onBlur={() => handleBlur()}
          onClick={onClick}
          value={value}
          style={style}
        />
        <KeyboardListener
          eventTarget={document}
          keyboardEvent='keyup'
          bindings={[
            KeyBinding(KeyCode.enter, handleBlur),
            KeyBinding(KeyCode.esc, handleEscKey)
          ]}
        />
      </>
    )
  }
  return (
    <div
      data-testid={`editable-text-${value}`}
      className={`relative ${editable ? 'text-cursor' : ''} ${css.edit} ${noRightPaddingClass} ${editable ? '' : css.nohover}`}
      title={value}
      onClick={() => { singleClickEdit && toggleEdit() }}
      onDoubleClick={() => { !singleClickEdit && toggleEdit() }}
      onFocus={toggleEdit}
      tabIndex={tabIndex ?? undefined}
    >
      {showIcon && <Pencil className='absolute' size={iconSize} style={iconStyle} />}
      {children(value || defaultValue || '', { className, style })}
    </div>
  )
}

export default EditableText
